/**
 * Foreground time accumulator, in the shape GA4's `engagement_time_msec` expects: the additional
 * engagement time since the previous event, not a running total.
 *
 * The browser loads no Google tag, so nothing else can supply this. Without it GA4's whole
 * engagement family — average engagement time, engaged sessions, engagement rate, bounce rate —
 * has no input at all. A constant would be worse than nothing: it turns "no data" into a
 * confidently wrong number, so this measures real visible time instead.
 *
 * Only visible time counts (a backgrounded tab is not engagement), and reading the value resets
 * the accumulator so the same milliseconds are never reported twice.
 */
const MAX_ENGAGEMENT_TIME_MSEC = 12 * 60 * 60 * 1_000;

let accumulatedMsec = 0;
let visibleSinceMsec: number | undefined;
let started = false;

const isDocumentVisible = (): boolean => typeof document === "undefined" || document.visibilityState === "visible";

const settle = (): void => {
  if (visibleSinceMsec === undefined) return;
  accumulatedMsec += Math.max(0, Date.now() - visibleSinceMsec);
  visibleSinceMsec = undefined;
};

const resume = (): void => {
  if (visibleSinceMsec !== undefined) return;
  visibleSinceMsec = Date.now();
};

/**
 * Starts accumulating. Idempotent, and safe to call before the document exists (SSR) — it simply
 * does nothing until a browser is present.
 */
export function startEngagementTimeTracking(): () => void {
  if (started || typeof document === "undefined") return () => {};
  started = true;

  const handleVisibilityChange = (): void => {
    if (isDocumentVisible()) resume();
    else settle();
  };

  if (isDocumentVisible()) resume();
  document.addEventListener("visibilitychange", handleVisibilityChange);

  return () => {
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    settle();
    started = false;
  };
}

/**
 * Returns the foreground milliseconds accumulated since the previous read and resets the counter.
 * Returns undefined when nothing has accumulated, so a zero is never sent as a parameter.
 */
export function consumeEngagementTimeMsec(): number | undefined {
  settle();
  const elapsed = Math.min(accumulatedMsec, MAX_ENGAGEMENT_TIME_MSEC);
  accumulatedMsec = 0;
  if (isDocumentVisible()) resume();
  return elapsed > 0 ? Math.round(elapsed) : undefined;
}

export function resetEngagementTimeForTests(): void {
  accumulatedMsec = 0;
  visibleSinceMsec = undefined;
  started = false;
}
